import { useState, useRef } from "react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useSearchCustomers } from "@/features/customers/hooks/useCustomer";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface CustomerSearchSelectProps {
  onSelect: (customerId: string, customerName: string) => void;
  selectedCustomerId?: string;
  selectedCustomerName?: string;
  disabled?: boolean;
}

export const CustomerSearchSelect = ({
  onSelect,
  selectedCustomerId,
  selectedCustomerName,
  disabled = false,
}: CustomerSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: customers = [], isLoading } =
    useSearchCustomers(debouncedSearch);
  const visibleCustomers = search.trim() === debouncedSearch.trim() && search.trim().length >= 2
    ? customers
    : [];

  const handleSelect = (customerId: string, customerName: string) => {
    onSelect(customerId, customerName);
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          ref={inputRef}
          placeholder="Search customer..."
          value={search || selectedCustomerName || ""}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
            if (selectedCustomerId) {
              onSelect("", "");
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={(e) => {
            // Only close if focus isn't moving into the popover content
            const relatedTarget = e.relatedTarget as Node | null;
            if (
              relatedTarget &&
              document
                .querySelector("[data-slot='popover-content']")
                ?.contains(relatedTarget)
            ) {
              return;
            }
            setOpen(false);
          }}
          disabled={disabled}
        />
      </PopoverAnchor>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)]"
        align="start"
        sideOffset={4}
        avoidCollisions
        collisionPadding={16}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (
            inputRef.current &&
            inputRef.current.contains(e.target as Node)
          ) {
            e.preventDefault();
          }
        }}
      >
        <div className="max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center">
              <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">
                Searching...
              </p>
            </div>
          ) : search.trim().length < 2 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search.
            </div>
          ) : visibleCustomers.length === 0 && search.trim() === debouncedSearch.trim() ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No customers found.
            </div>
          ) : (
            visibleCustomers.map((customer) => (
              <div
                key={customer.id}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm",
                  selectedCustomerId === customer.id && "bg-accent"
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(customer.id, customer.customerName);
                }}
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    selectedCustomerId === customer.id
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />
                <div className="flex flex-col">
                  <span>{customer.customerName}</span>
                  <span className="text-xs text-muted-foreground">
                    {customer.mobileNo1}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
